import type { DocPackPage, DocPackPageWord } from '@listening/doc-pack';

export type DocPackSearchMatch = {
  page: number;
  word: DocPackPageWord;
  wordIdx: number;
};

const normalizedWords = (query: string) =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

export const docPackWordSearchKey = (word: {
  page: number;
  chars: readonly [number, number];
}) =>
  `doc-pack:${word.page.toString()}:${word.chars[0].toString()}-${word.chars[1].toString()}`;

export function findDocPackSearchMatches(
  pages: readonly DocPackPage[],
  query: string,
): readonly DocPackSearchMatch[] {
  const queryWords = normalizedWords(query);
  if (queryWords.length === 0) return [];

  const matches: DocPackSearchMatch[] = [];
  for (const page of pages.toSorted((a, b) => a.page - b.page)) {
    const words = page.words;
    if (queryWords.length === 1) {
      const singleQuery = queryWords[0];
      if (!singleQuery) return matches;
      for (const [wordIdx, word] of words.entries()) {
        if (word.text.toLowerCase().includes(singleQuery)) {
          matches.push({ page: page.page, word, wordIdx });
        }
      }
      continue;
    }

    // Overlapping windows (e.g. 'the the the' vs query 'the the') would push
    // the same word more than once — dedupe so navigation and UI keys stay
    // unique. Ascending iteration keeps first-occurrence order.
    const matchedWordIdxs = new Set<number>();
    for (
      let wordIdx = 0;
      wordIdx <= words.length - queryWords.length;
      wordIdx++
    ) {
      const wordsSlice = words.slice(wordIdx, wordIdx + queryWords.length);
      const allMatch = queryWords.every((queryWord, idx) =>
        wordsSlice[idx]?.text.toLowerCase().includes(queryWord),
      );
      if (!allMatch) continue;
      for (const [offset, word] of wordsSlice.entries()) {
        const matchedWordIdx = wordIdx + offset;
        if (matchedWordIdxs.has(matchedWordIdx)) continue;
        matchedWordIdxs.add(matchedWordIdx);
        matches.push({
          page: page.page,
          word,
          wordIdx: matchedWordIdx,
        });
      }
    }
  }
  return matches;
}
